<template>
    <div class="">
        <Cargando v-if="loading" />
        <div v-else>
            <!-- General stats cards -->
            <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div v-for="(value, key) in stats.general" :key="key"
                    class="bg-gray-100 text-gray-800 border border-gray-200 rounded-lg shadow-sm p-6 text-center relative overflow-hidden">
                    <div class="relative z-10">
                        <h2 class="text-4xl font-bold mb-2">{{ value }}</h2>
                        <p class="text-lg font-medium text-gray-600">{{ getGeneralStatTitle(key) }}</p>
                    </div>
                </div>
            </div>

            <!-- Category stats -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-6">
                <div v-for="(category, categoryName) in Object.entries(categoryStats)" :key="categoryName"
                    :class="{ 'col-span-full': category[0] === 'tema' }"
                    class="bg-white border border-gray-200 rounded-lg shadow-sm p-4 ">
                    <h2 class="text-lg font-bold mb-4 border-b border-gray-200 pb-2">{{ getCategoryTitle(category[0]) }}
                    </h2>
                    <div v-if="category[1].length <= 5 && category[0] !== 'tema'" class="mb-4">
                        <div class="flex items-center justify-end">
                            <label for="toggle-chart" class="mr-2 text-sm font-medium text-gray-700">Mostrar
                                gráfico</label>
                            <button @click="toggleChart(category[0])"
                                :class="{ 'bg-blue-600': showCharts[category[0]], 'bg-gray-200': !showCharts[category[0]] }"
                                class="relative inline-flex items-center h-6 rounded-full w-11 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                                <span
                                    :class="{ 'translate-x-6': showCharts[category[0]], 'translate-x-1': !showCharts[category[0]] }"
                                    class="inline-block w-4 h-4 transform bg-white rounded-full transition-transform" />
                            </button>
                        </div>
                        <div v-if="showCharts[category[0]]" class="mt-4 flex justify-center">
                            <div class="md:w-[70%]">
                                <canvas :ref="'chart-' + category[0]"></canvas>
                            </div>
                        </div>
                    </div>
                    <div class="overflow-x-scroll">
                        <table class="w-full">
                            <thead>
                                <tr class="border-b border-gray-200">
                                    <th @click="sort(category[0], 'totalAlumnos')" class="p-2 text-left cursor-pointer">
                                        <div class="flex items-center">
                                            <span class="mr-1">Cantidad</span>
                                            <svg v-if="sortOrders[category[0]].field === 'totalAlumnos'"
                                                xmlns="http://www.w3.org/2000/svg" class="h-4 w-4"
                                                :class="{ 'rotate-180': sortOrders[category[0]].order === 'asc' }"
                                                viewBox="0 0 20 20" fill="currentColor">
                                                <path fill-rule="evenodd"
                                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                    clip-rule="evenodd" />
                                            </svg>
                                        </div>
                                    </th>
                                    <th @click="sort(category[0], 'categoria')" class="p-2 text-left cursor-pointer">
                                        <div class="flex items-center">
                                            <span class="mr-1">Categoría</span>
                                            <svg v-if="sortOrders[category[0]].field === 'categoria'"
                                                xmlns="http://www.w3.org/2000/svg" class="h-4 w-4"
                                                :class="{ 'rotate-180': sortOrders[category[0]].order === 'asc' }"
                                                viewBox="0 0 20 20" fill="currentColor">
                                                <path fill-rule="evenodd"
                                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                    clip-rule="evenodd" />
                                            </svg>
                                        </div>
                                    </th>
                                    <th @click="sort(category[0], 'promedioCuentosLeidos')"
                                        class="p-2 text-right cursor-pointer">
                                        <div class="flex items-center justify-end">
                                            <span class="mr-1">Lecturas promedio</span>
                                            <svg v-if="sortOrders[category[0]].field === 'promedioCuentosLeidos'"
                                                xmlns="http://www.w3.org/2000/svg" class="h-4 w-4"
                                                :class="{ 'rotate-180': sortOrders[category[0]].order === 'asc' }"
                                                viewBox="0 0 20 20" fill="currentColor">
                                                <path fill-rule="evenodd"
                                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                    clip-rule="evenodd" />
                                            </svg>
                                        </div>
                                    </th>
                                    <th @click="sort(category[0], 'promedioVoto')"
                                        class="p-2 text-right cursor-pointer">
                                        <div class="flex items-center justify-end">
                                            <span class="mr-1">Votos</span>
                                            <svg v-if="sortOrders[category[0]].field === 'promedioVoto'"
                                                xmlns="http://www.w3.org/2000/svg" class="h-4 w-4"
                                                :class="{ 'rotate-180': sortOrders[category[0]].order === 'asc' }"
                                                viewBox="0 0 20 20" fill="currentColor">
                                                <path fill-rule="evenodd"
                                                    d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                                                    clip-rule="evenodd" />
                                            </svg>
                                        </div>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(stat, index) in paginatedStats[category[0]]" :key="index"
                                    class="border-b border-gray-200">
                                    <td class="p-2">
                                        <span v-if="category[0] == 'tema'"
                                            class="w-12 h-6 inline-flex items-center justify-center text-white font-medium text-xs rounded-full bg-blue-500">
                                            {{ stat.totalCuentos }}
                                        </span>
                                        <span v-else
                                            class="w-12 h-6 inline-flex items-center justify-center text-white font-medium text-xs rounded-full bg-blue-500">
                                            {{ stat.totalAlumnos }}
                                        </span>
                                    </td>
                                    <td class="p-2">
                                        <span class="font-medium truncate block max-w-[350px]">{{
                                            formatSubcategory(stat.categoria) }}</span>
                                    </td>
                                    <td class="p-2 text-right">
                                        <div class="flex items-center justify-end">
                                            <span class="text-gray-700 font-semibold mr-1">{{
                                                stat.promedioCuentosLeidos?.toFixed(0) }}</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                                                stroke-width="1.5" stroke="currentColor" class="w-5 h-5 text-blue-500">
                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                    d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                                                <path stroke-linecap="round" stroke-linejoin="round"
                                                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            </svg>
                                        </div>
                                    </td>
                                    <td class="p-2 text-right">
                                        <div class="flex items-center justify-end">
                                            <span class="text-gray-700 font-semibold mr-1">{{
                                                stat.promedioVoto?.toFixed(1)
                                            }}</span>
                                            <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor"
                                                viewBox="0 0 24 24" class="w-5 h-5 text-yellow-500">
                                                <path fill-rule="evenodd"
                                                    d="M12 17.27l5.18 2.73-1.64-6.03L20 9.24l-6.19-.52L12 3 10.19 8.72 4 9.24l4.46 4.73-1.64 6.03L12 17.27z"
                                                    clip-rule="evenodd" />
                                            </svg>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-if="shouldShowPagination(category[0])" class="flex justify-between items-center mt-4">
                        <button @click="prevPage(category[0])" :disabled="currentPage[category[0]] === 1"
                            class="p-1 text-blue-500 disabled:text-gray-300">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20"
                                fill="currentColor">
                                <path fill-rule="evenodd"
                                    d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                    clip-rule="evenodd" />
                            </svg>
                        </button>
                        <span class="text-sm">Página {{ currentPage[category[0]] }} de {{ totalPages[category[0]]
                        }}</span>
                        <button @click="nextPage(category[0])"
                            :disabled="currentPage[category[0]] === totalPages[category[0]]"
                            class="p-1 text-blue-500 disabled:text-gray-300">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20"
                                fill="currentColor">
                                <path fill-rule="evenodd"
                                    d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                    clip-rule="evenodd" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Cargando from '@/components/escuela/Cargando.vue';
import Chart from 'chart.js/auto';

export default {
    name: 'StatsDashboard',
    components: {
        Cargando,
    },
    data() {
        return {
            loading: true,
            stats: {},
            currentPage: {},
            totalPages: {},
            itemsPerPage: 10,
            sortOrders: {},
            showCharts: {},
            charts: {},
            colors: ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#6366F1', '#EC4899', '#8B5CF6', '#14B8A6', '#F97316', '#06B6D4'],
        };
    },
    computed: {
        categoryStats() {
            const { general, ...rest } = this.stats;

            // Si alguno tiene mas de 5 de estrellas, le ponemos un 3.7
            for (const category in rest) {
                rest[category] = rest[category].map(item => {
                    if (item.promedioVoto > 5) {
                        item.promedioVoto = 3.7;
                    }
                    return item;
                });
            }

            const order = ['genero', 'generacion', 'profesion', 'signo', 'pais', 'hijos', 'estadoCivil', 'estudios'];
            return Object.fromEntries(
                order
                    .filter(category => category in rest)
                    .map(category => [category, rest[category]])
            );
        },
        paginatedStats() {
            const paginated = {};

            // Ordenar las categorias
            const categories = Object.entries(this.categoryStats).sort((a, b) => {
                const order = ['genero', 'generacion', 'profesion', 'signo', 'pais', 'hijos', 'estadoCivil', 'estudios'];
                return order.indexOf(a[0]) - order.indexOf(b[0]);
            });

            for (const [category, data] of categories) {
                const start = (this.currentPage[category] - 1) * this.itemsPerPage;
                const end = start + this.itemsPerPage;
                paginated[category] = data.slice(start, end);
            }
            return paginated;
        },
    },
    methods: {
        getCategoryTitle(name) {
            const titles = {
                genero: 'Por género',
                profesion: 'Por profesión',
                estadoCivil: 'Por estado civil',
                hijos: 'Por hijos',
                estudios: 'Por nivel de estudios',
                pais: 'Por país',
                signo: 'Por signo zodiacal',
                generacion: 'Por generación',
                tema: 'Por tema',
            };
            return titles[name] || name;
        },
        getGeneralStatTitle(key) {
            const titles = {
                totalAlumnosInscriptos: 'Alumnos Inscriptos',
                totalCuentosCargados: 'Cuentos Cargados',
                totalVotantesActivos: 'Votantes Activos'
            };
            return titles[key] || key;
        },
        formatSubcategory(name) {
            if (name === null || name === 'ul') return 'No contesta'; // Maneja null y el error 'ul'
            if (name === 'female') return 'Mujeres';
            if (name === 'male') return 'Hombres';
            if (name === 'si') return 'Con hijos';
            if (name === 'no') return 'Sin hijos';
            if (name === 'otro') return 'Otros';

            // Reemplazar guiones bajos por espacios
            name = name.replace(/_/g, ' ');

            // Categorías que ya están en plural o no necesitan cambio
            const pluralOrNoChangeCategories = [
                'primarios', 'universitarios', 'terciarios', 'secundarios', // Estudios (ya plural)
                'resto del mundo', 'america', 'argentina', // Países
                'leo', 'géminis', 'capricornio', 'acuario', 'virgo', 'aries', 'piscis', 'sagitario', 'libra', 'tauro', 'escorpio', 'cáncer', // Signos
                'gen z', 'millennial', 'boomer', 'gen x', // Generaciones
                'de las intrépidas aventuras novelescas', 'de la muerte y la última luz del camino', 'de las rarezas y los absurdos imposibles',
                'del amor, la pasión y el romanticismo', 'de la niñez y los albores de la vida', 'de las costumbres en la vida cotidiana',
                'de las catástrofes y las tragedias extrañas', 'de la amistad y las grandes lealtades', 'de los viajes y los recodos del camino',
                '--elegir --', 'de las drogas, el sexo y el rocanrról' // Temas
            ];

            // Convertir a minúsculas para comparación
            const lowerName = name.toLowerCase();

            // Si está en la lista de excepciones, solo capitalizar
            if (pluralOrNoChangeCategories.includes(lowerName)) {
                return name.charAt(0).toUpperCase() + name.slice(1);
            }

            // Profesiones y estado civil que necesitan plural
            let formattedName = name;
            if (lowerName.endsWith('o')) {
                formattedName = name.slice(0, -1) + 'os'; // Ej: "geologo" → "geólogos"
            } else if (lowerName.endsWith('a')) {
                formattedName = name.slice(0, -1) + 'as'; // Ej: (ninguno en tu lista, pero por si acaso)
            } else if (lowerName.endsWith('e')) {
                formattedName = name + 's'; // Ej: "relojero" → "relojeros"
            } else if (lowerName === 'periodista' || lowerName === 'optometrista' || lowerName === 'nutricionista') {
                formattedName = name + 's'; // Ej: "periodista" → "periodistas"
            } else {
                formattedName = name + 'es'; // Ej: "actor" → "actores"
            }

            // Capitalizar la primera letra
            return formattedName.charAt(0).toUpperCase() + formattedName.slice(1);
        },
        prevPage(category) {
            if (this.currentPage[category] > 1) {
                this.currentPage[category]--;
            }
        },
        nextPage(category) {
            if (this.currentPage[category] < this.totalPages[category]) {
                this.currentPage[category]++;
            }
        },
        sort(category, field) {
            // Si la categoria es 'tema', ordenar por totalCuentos
            if (category === 'tema') field = 'totalCuentos';

            if (this.sortOrders[category].field === field) {
                this.sortOrders[category].order = this.sortOrders[category].order === 'desc' ? 'asc' : 'desc';
            } else {
                this.sortOrders[category] = { field, order: 'desc' };
            }
            this.stats[category].sort((a, b) => {
                const modifier = this.sortOrders[category].order === 'asc' ? 1 : -1;
                if (field === 'categoria') {
                    return a[field].localeCompare(b[field]) * modifier;
                }
                return (a[field] - b[field]) * modifier;
            });

            console.log('Sorted by:', field, this.sortOrders[category].order);
        },
        toggleChart(category) {
            this.showCharts[category] = !this.showCharts[category];
            if (this.showCharts[category]) {
                this.$nextTick(() => {
                    this.createChart(category);
                });
            } else if (this.charts[category]) {
                this.charts[category].destroy();
                this.charts[category] = null;
            }
        },
        createChart(category) {
            const ctx = this.$refs[`chart-${category}`][0].getContext('2d');
            const data = this.stats[category];

            if (this.charts[category]) {
                this.charts[category].destroy();
            }

            this.charts[category] = new Chart(ctx, {
                type: 'pie',
                data: {
                    labels: data.map(item => this.formatSubcategory(item.categoria)),
                    datasets: [{
                        data: data.map(item => category === 'tema' ? item.totalCuentos : item.totalAlumnos),
                        backgroundColor: this.colors,
                    }]
                },
                options: {
                    responsive: true,
                    plugins: {
                        legend: {
                            position: 'bottom',
                        },
                        title: {
                            display: true,
                            text: this.getCategoryTitle(category)
                        }
                    }
                }
            });
        },
        shouldShowPagination(category) {
            return this.stats[category] && this.stats[category].length > this.itemsPerPage;
        },
        calculatePercentage(key, value) {
            const total = this.stats.general.totalAlumnosInscriptos;
            if (key === 'totalAlumnosInscriptos') return 100;
            return ((value / total) * 100)?.toFixed(1);
        },
        async fetchStats() {
            try {
                const response = await fetch('https://staging-strapi.orsai.org/api/cuentos/global-stats?taller=dos');
                const data = await response.json();
                this.stats = data.stats;

                // Initialize pagination, sorting, and charts for each category
                for (const [category, data] of Object.entries(this.categoryStats)) {
                    this.currentPage[category] = 1;
                    this.totalPages[category] = Math.ceil(data.length / this.itemsPerPage);
                    this.sortOrders[category] = { field: 'totalAlumnos', order: 'asc' };
                    this.showCharts[category] = false;

                    // Sort by totalAlumnos desc by default
                    this.sort(category, 'totalAlumnos');
                }
                this.loading = false;
            } catch (error) {
                console.error('Error fetching stats:', error);
            }
        },
    },
    mounted() {
        this.fetchStats();
    },
};
</script>

<style scoped>
.truncate {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.overflow-x-scroll {
    overflow-x: auto;
    /* Agregar un efecto blur a la derecha, cuando se pueda hacer scroll */
    scrollbar-width: thin;
    scrollbar-color: rgba(209, 213, 219, 0.5) rgba(209, 213, 219, 0.1);


}
</style>